// Markdown helpers shared by the SEO analyser, exporters and the editor.
// Japanese text has no word boundaries, so "length" is counted in characters.

export interface Heading {
  level: number;
  text: string;
  line: number;
  id: string;
}

export function extractHeadings(markdown: string): Heading[] {
  const headings: Heading[] = [];
  let inFence = false;
  markdown.split("\n").forEach((raw, line) => {
    if (/^\s*```/.test(raw)) inFence = !inFence;
    if (inFence) return;
    const m = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(raw);
    if (m) {
      const text = m[2]!.trim();
      headings.push({ level: m[1]!.length, text, line, id: headingId(text, headings.length) });
    }
  });
  return headings;
}

export function headingId(text: string, index: number): string {
  const base = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return `h-${index}-${base || "section"}`;
}

/** Plain text with markdown syntax removed, for counting and excerpts. */
export function stripMarkdown(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\|?\s*:?-{3,}.*$/gm, "")
    .replace(/\|/g, " ")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/^-{3,}$/gm, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Characters of readable content, excluding whitespace. */
export function countChars(markdown: string): number {
  return stripMarkdown(markdown).replace(/\s/g, "").length;
}

export function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[。！？!?])|\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function paragraphs(markdown: string): string[] {
  return markdown
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^(#{1,6}\s|```|\||[-*+]\s|\d+\.\s|>)/.test(p));
}

export function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  let count = 0;
  let i = h.indexOf(n);
  while (i !== -1) {
    count++;
    i = h.indexOf(n, i + n.length);
  }
  return count;
}

export function excerpt(markdown: string, max = 120): string {
  const text = stripMarkdown(markdown).replace(/\s+/g, " ").trim();
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * URL slug. Latin text is kebab-cased; text with no latin characters falls
 * back to a short stable hash so Japanese titles still get a usable slug.
 */
export function slugify(input: string): string {
  const latin = input
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  if (latin.length >= 3) return latin;
  return `column-${hash(input)}`;
}

function hash(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/** Reading time in minutes at ~500 Japanese characters per minute. */
export function readingMinutes(markdown: string): number {
  return Math.max(1, Math.round(countChars(markdown) / 500));
}

interface SectionRange {
  start: number;
  end: number;
}

/** Line range of the section under a heading, up to the next heading at the same or a higher level. */
function sectionRange(lines: string[], heading: string, level: number): SectionRange | null {
  const target = heading.trim();
  let inFence = false;
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*```/.test(lines[i]!)) inFence = !inFence;
    if (inFence) continue;
    const m = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(lines[i]!);
    if (!m) continue;
    const l = m[1]!.length;
    if (start === -1) {
      if (l === level && m[2]!.trim() === target) start = i;
    } else if (l <= level) {
      return { start, end: i };
    }
  }
  return start === -1 ? null : { start, end: lines.length };
}

export function extractSection(markdown: string, heading: string, level: number): string {
  const lines = markdown.split("\n");
  const r = sectionRange(lines, heading, level);
  return r ? lines.slice(r.start, r.end).join("\n").trim() : "";
}

/**
 * Replace the section under a heading with new markdown. If the heading is
 * not in the body yet, the section is appended.
 */
export function replaceSection(markdown: string, heading: string, level: number, replacement: string): string {
  const lines = markdown.split("\n");
  const r = sectionRange(lines, heading, level);
  const block = replacement.trim();
  if (!r) return `${markdown.trimEnd()}\n\n${block}\n`;
  const before = lines.slice(0, r.start).join("\n").trimEnd();
  const after = lines.slice(r.end).join("\n").trim();
  return [before, block, after].filter(Boolean).join("\n\n") + "\n";
}

/**
 * The body of the H2 section titled `heading` (up to the next H2), and a way
 * to put a new body in its place. Fenced code is ignored when looking for
 * headings. null when no H2 has that exact text.
 */
export function findSection(markdown: string, heading: string): { body: string; replace: (body: string) => string } | null {
  const lines = markdown.split("\n");
  let inFence = false;
  const h2: number[] = [];
  lines.forEach((l, i) => {
    if (/^\s*```/.test(l)) inFence = !inFence;
    if (!inFence && /^##\s+/.test(l)) h2.push(i);
  });
  const start = h2.find((i) => lines[i]!.replace(/^##\s+/, "").replace(/\s*#*\s*$/, "").trim() === heading.trim());
  if (start === undefined) return null;
  const end = h2.find((i) => i > start) ?? lines.length;
  const body = lines.slice(start + 1, end).join("\n").trim();
  return {
    body,
    replace: (next) => [...lines.slice(0, start + 1), "", next.trim(), "", ...lines.slice(end)].join("\n").replace(/\n{3,}/g, "\n\n"),
  };
}
